import { KpiSeries, KpiSeriesWithImage } from "../../shared/types";

import gtWalsheimRegularWoff2Url from "../../../../common/fonts/gt-walsheim/GT-Walsheim-Pro-Regular.woff2";

const getPng = (width: number, height: number, scale: number, svgString: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
      }
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Canvas toBlob failed"));
        resolve(URL.createObjectURL(blob));
      }, "image/png");
      URL.revokeObjectURL(url); // done with the source blob URL now that it's drawn
    };
    img.onerror = reject;
    const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  });
};
const RENDERABLE_TAGS = new Set(["text", "tspan", "path", "line", "rect", "circle", "polyline", "polygon"]);

const inlineComputedStyles = (source: Element, clone: Element) => {
  if (RENDERABLE_TAGS.has(source.tagName.toLowerCase())) {
    const computed = getComputedStyle(source);
    const relevantProps = [
      "font-family",
      "font-size",
      "font-weight",
      "fill",
      "stroke",
      "stroke-width",
      "stroke-dasharray",
      "text-anchor",
    ];
    const styleString = relevantProps.map((prop) => `${prop}:${computed.getPropertyValue(prop)}`).join(";");
    clone.setAttribute("style", styleString);
  }

  const sourceChildren = Array.from(source.children);
  const cloneChildren = Array.from(clone.children);
  sourceChildren.forEach((child, i) => {
    if (cloneChildren[i]) inlineComputedStyles(child, cloneChildren[i]);
  });
};
let embeddedFontStylePromise: Promise<string> | null = null;
const embedFont = async (svg: SVGSVGElement, fontFamily: string, fontUrl: string) => {
  if (!embeddedFontStylePromise) {
    embeddedFontStylePromise = fetch(fontUrl)
      .then((res) => res.arrayBuffer())
      .then((buffer) => btoa(Array.from(new Uint8Array(buffer), (b) => String.fromCharCode(b)).join("")))
      .then(
        (fontBase64) => `
            @font-face {
              font-family: "${fontFamily}";
              src: url("data:font/woff2;base64,${fontBase64}") format("woff2");
            }
          `,
      );
  }

  const styleEl = document.createElementNS("http://www.w3.org/2000/svg", "style");
  styleEl.textContent = await embeddedFontStylePromise;
  svg.insertBefore(styleEl, svg.firstChild);
};

const boostGridLines = (svg: SVGSVGElement) => {
  svg.querySelectorAll(".recharts-cartesian-grid line, .recharts-cartesian-axis-line").forEach((line) => {
    (line as SVGLineElement).style.stroke = "#c4c4c4";
    (line as SVGLineElement).style.strokeWidth = "1.5";
  });
};
const svgToPngDataUrlForSerires = async (svgElement: SVGSVGElement, scale = 3): Promise<string> => {
  const width = Number(svgElement.getAttribute("width")) || svgElement.clientWidth;
  const height = Number(svgElement.getAttribute("height")) || svgElement.clientHeight;

  const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svgElement, clonedSvg);
  boostGridLines(clonedSvg);
  await embedFont(clonedSvg, "GTWalsheimPro", gtWalsheimRegularWoff2Url);

  clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clonedSvg.setAttribute("width", `${width}`);
  clonedSvg.setAttribute("height", `${height}`);
  clonedSvg.setAttribute("shape-rendering", "crispEdges");

  const svgString = new XMLSerializer().serializeToString(clonedSvg);

  return getPng(width, height, scale, svgString);
};

const waitForStableElement = async <T extends Element = HTMLElement>(
  container: Element,
  selector = "",
  maxAttempts = 20,
): Promise<T | null> => {
  let lastWidth = -1;
  for (let i = 0; i < maxAttempts; i++) {
    // Uses setTimeout, not requestAnimationFrame: rAF callbacks are suspended by browsers
    // when the tab is backgrounded, which would hang this poll forever. setTimeout keeps
    // firing (throttled) in the background.
    await new Promise((r) => setTimeout(r, 16));
    let svg;
    if (!selector) {
      svg = container.querySelector<T>("svg");
    }
    if (selector) {
      svg = container.querySelector<T>(selector);
    }
    const width = svg?.clientWidth ?? 0;
    if (svg && width > 0 && width === lastWidth) {
      return svg;
    }
    lastWidth = width;
  }
  return container.querySelector<T>(selector);
};

const waitForElement = async (selector: string, maxAttempts = 20): Promise<Element | null> => {
  for (let i = 0; i < maxAttempts; i++) {
    const el = document.querySelector(selector);
    if (el) return el;
    // See waitForStableElement above: setTimeout, not requestAnimationFrame, so this poll
    // doesn't hang if the tab is backgrounded.
    await new Promise((r) => setTimeout(r, 16));
  }
  return document.querySelector(selector);
};

export const getSvg = async (
  className: string,
  selector: string,
  scale = 3,
  isGraph = false,
): Promise<string | null> => {
  const container = await waitForElement(`.${className}`);
  if (!container) return null;
  const svgElement = await waitForStableElement<SVGSVGElement>(container, selector);
  if (!svgElement) return null;

  if (isGraph) {
    return svgToPngDataUrlForSerires(svgElement, scale);
  } else {
    const width = Number(svgElement.getAttribute("width")) || svgElement.clientWidth;
    const height = Number(svgElement.getAttribute("height")) || svgElement.clientHeight;
    const svgString = new XMLSerializer().serializeToString(svgElement);
    return getPng(width, height, scale, svgString);
  }
};

const getNoDataImage = async (searchId: string): Promise<string | null> => {
  const container = document.querySelector(`.${searchId}`);
  if (!container) return null;
  const iconEl = await waitForStableElement(container, ".icon-no-data");
  if (!iconEl) return null;

  const bgImage = getComputedStyle(iconEl).backgroundImage;
  const urlMatch = bgImage.match(/url\(["']?(.*?)["']?\)/);
  const svgUrl = urlMatch?.[1];
  if (!svgUrl) return null;

  const response = await fetch(svgUrl);
  const svgString = await response.text();
  const scale = 5;
  const width = iconEl.clientWidth;
  const height = iconEl.clientHeight;

  return getPng(width, height, scale, svgString);
};

export const addImageToSeries = async (series?: KpiSeries[] | null): Promise<KpiSeriesWithImage[] | null> => {
  if (!series) return null;
  return Promise.all(
    series.map(async (s) => {
      let chartImage = (await getSvg(`${s.kpi}-searchId`, "svg.recharts-surface", undefined, true)) ?? null;
      if (chartImage === null) {
        chartImage = await getNoDataImage(`leaf-error-message__error-icon`);
      }
      return {
        ...s,
        chartImage,
      };
    }),
  );
};

export const getFlagImage = async (searchId: string): Promise<string | null> => {
  const container = document.getElementById(searchId);
  if (!container) return null;

  const svgElement = container.querySelector<SVGSVGElement>("svg");
  if (svgElement) {
    const width = Number(svgElement.getAttribute("width")) || svgElement.clientWidth;
    const height = Number(svgElement.getAttribute("height")) || svgElement.clientHeight;
    const svgString = new XMLSerializer().serializeToString(svgElement);
    return getPng(width, height, 3, svgString);
  }

  const iconEl = container.querySelector<HTMLElement>("span");
  if (!iconEl) return null;

  const bgImage = getComputedStyle(iconEl).backgroundImage;
  const urlMatch = bgImage.match(/url\(["']?(.*?)["']?\)/);
  const svgUrl = urlMatch?.[1];
  if (!svgUrl) return null;

  const response = await fetch(svgUrl);
  const svgString = await response.text();
  const width = iconEl.clientWidth;
  const height = iconEl.clientHeight;
  const scale = 3;

  return getPng(width, height, scale, svgString);
};
